/**
 * บล็อกแสดง "ของชิ้นนี้คืออะไร" ในใบตามเก็บอุปกรณ์ — ใช้ร่วมกันทุกจอ
 *
 * คู่กับ `utils/returnItemLabel.ts` (ตรรกะ) — ไฟล์นี้คือหน้าตา
 * เหตุผลที่ต้องมีที่เดียว: ก่อนหน้านี้แดชบอร์ด / โมดัลติดตาม / โมดัลรายละเอียด
 * ต่างคนต่างวาด แล้วทุกจอวาง "ซิม" เป็นข้อมูลประกอบของเครื่อง จน Support
 * แยกไม่ออกว่าแถวไหนคือซิมที่หายไปชิ้นเดียว (feedback ทิวากร)
 *
 * ไอคอน + สีต้องแยกเครื่องกับซิมได้ในพริบตา:
 *   เครื่อง = ไอคอนอุปกรณ์ พื้นน้ำเงิน (primary)
 *   ซิม     = ไอคอนซิม พื้นฟ้า (info)
 */
import React from 'react';
import { RiDeviceLine, RiSimCardLine, RiStackLine, RiQuestionLine } from 'react-icons/ri';
import {
  describeReturnItem,
  LEGACY_COMBINED_HINT,
  type ReturnItemDescriptor,
  type ReturnItemKind,
  type ReturnItemLike,
} from '../../utils/returnItemLabel';

interface KindStyle {
  /** สีของ badge ประเภท */
  badge: string;
  /** สีแถบ/ไอคอน */
  icon: string;
  Icon: React.ComponentType<{ className?: string }>;
}

export const RETURN_ITEM_KIND_STYLE: Record<ReturnItemKind, KindStyle> = {
  DEVICE: {
    badge: 'bg-primary-100 text-primary-800 border-primary-200',
    icon: 'bg-primary-600',
    Icon: RiDeviceLine,
  },
  SIM: {
    badge: 'bg-info-100 text-info-800 border-info-200',
    icon: 'bg-info-600',
    Icon: RiSimCardLine,
  },
  COMBINED: {
    badge: 'bg-warning-100 text-warning-800 border-warning-200',
    icon: 'bg-warning-600',
    Icon: RiStackLine,
  },
  UNKNOWN: {
    badge: 'bg-secondary-100 text-secondary-700 border-secondary-200',
    icon: 'bg-secondary-500',
    Icon: RiQuestionLine,
  },
};

/** badge ประเภทเล็ก ๆ — "เครื่อง" / "ซิม" */
export const ReturnItemKindBadge: React.FC<{
  item?: ReturnItemLike;
  descriptor?: ReturnItemDescriptor;
  className?: string;
}> = ({ item, descriptor, className = '' }) => {
  const d = descriptor ?? describeReturnItem(item ?? {});
  const style = RETURN_ITEM_KIND_STYLE[d.kind];
  const { Icon } = style;

  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-semibold rounded border ${style.badge} ${className}`}
      title={d.isLegacyCombined ? LEGACY_COMBINED_HINT : `ประเภท: ${d.kindLabel}`}
    >
      <Icon className="w-3 h-3 flex-shrink-0" />
      {d.kindLabel}
    </span>
  );
};

/**
 * บล็อกระบุตัวของ — badge ประเภท + บรรทัดหลัก/รอง/สาม
 * `size="lg"` ใช้ในหัวโมดัลรายละเอียด · ค่าเริ่มต้นใช้ในตาราง/การ์ด
 */
export const ReturnItemIdentity: React.FC<{
  item: ReturnItemLike;
  size?: 'md' | 'lg';
  /** ซ่อน badge ประเภทเมื่อจอนั้นมีคอลัมน์ประเภทแยกอยู่แล้ว */
  hideKindBadge?: boolean;
  className?: string;
}> = ({ item, size = 'md', hideKindBadge = false, className = '' }) => {
  const d = describeReturnItem(item);
  const style = RETURN_ITEM_KIND_STYLE[d.kind];
  const { Icon } = style;

  if (size === 'lg') {
    return (
      <div className={`flex items-start gap-3 ${className}`}>
        <div
          className={`flex-shrink-0 w-10 h-10 ${style.icon} rounded-lg flex items-center justify-center`}
        >
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0">
          {!hideKindBadge && <ReturnItemKindBadge descriptor={d} className="mb-1" />}
          <div className="text-lg font-semibold text-secondary-900 break-words">{d.primary}</div>
          {d.secondary && <div className="text-sm text-secondary-600 mt-0.5">{d.secondary}</div>}
          {d.tertiary && <div className="text-sm text-secondary-500 mt-0.5">{d.tertiary}</div>}
          {d.isLegacyCombined && (
            <div className="text-xs text-warning-700 mt-1" title={LEGACY_COMBINED_HINT}>
              แถวนี้ถือทั้งเครื่องและซิม (ปิดก่อนวันปรับปรุงระบบ) จึงบันทึกสภาพแยกชิ้นไม่ได้ —
              ถ้าต้องแก้ ให้กด “เปิดรายการใหม่” ระบบจะแยกเป็น 2 แถวให้
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`min-w-0 ${className}`}>
      {!hideKindBadge && <ReturnItemKindBadge descriptor={d} className="mb-1" />}
      <div className="font-medium text-secondary-900 break-words">{d.primary}</div>
      {d.secondary && <div className="text-sm text-secondary-500">{d.secondary}</div>}
      {d.tertiary && <div className="text-xs text-secondary-400">{d.tertiary}</div>}
      {d.isLegacyCombined && (
        <div className="text-xs text-warning-700 mt-0.5" title={LEGACY_COMBINED_HINT}>
          เครื่อง + ซิม ในแถวเดียว (ปิดก่อนวันปรับปรุงระบบ)
        </div>
      )}
    </div>
  );
};

export default ReturnItemIdentity;
